import { useState } from "react";
import { Icon } from "@iconify/react";
import Link from "next/link";

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
const PHONE_REGEX = /^(?:(?:\+|00)33|0)\s*[1-9](?:[\s.-]*\d{2}){4}$/;

const ContactForm = () => {
  const [formData, setFormData] = useState({
    nom: "",
    prenom: "",
    email: "",
    telephone: "",
    message: "",
  });
  const [errors, setErrors] = useState<{
    [key: string]: string;
  }>({});
  const [status, setStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });
  const [isLoading, setIsLoading] = useState(false);

  const socialLinks = [
    {
      name: "Instagram",
      icon: "formkit:instagram",
      href: "https://www.instagram.com/salonsalexandragrey/",
      description: "Découvrez nos dernières réalisations",
    },
    {
      name: "Facebook",
      icon: "formkit:facebook",
      href: "https://www.facebook.com/SalonsAlexandraGrey",
      description: "Suivez notre actualité",
    },
    {
      name: "Linkedin",
      icon: "formkit:linkedin",
      href: "https://www.linkedin.com/company/alexandra-grey",
      description: "Rejoignez notre réseau professionnel",
    },
  ];

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.nom.trim()) {
      newErrors.nom = "Le nom est requis";
    }

    if (!formData.prenom.trim()) {
      newErrors.prenom = "Le prénom est requis";
    }

    if (!formData.email.trim()) {
      newErrors.email = "L'email est requis";
    } else if (!EMAIL_REGEX.test(formData.email)) {
      newErrors.email = "Format d'email invalide";
    }

    if (formData.telephone.trim() && !PHONE_REGEX.test(formData.telephone)) {
      newErrors.telephone = "Format de téléphone invalide (format français)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    setStatus({ type: null, message: "" });

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Une erreur s'est produite");
      }

      setStatus({
        type: "success",
        message: "Votre message a été envoyé avec succès !",
      });
      setFormData({
        nom: "",
        prenom: "",
        email: "",
        telephone: "",
        message: "",
      });
    } catch (error) {
      setStatus({
        type: "error",
        message:
          error instanceof Error
            ? error.message
            : "Une erreur s'est produite lors de l'envoi du message",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Effacer l'erreur quand l'utilisateur commence à corriger
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: "",
      });
    }
  };

  return (
    <div className="max-w-8xl mx-auto w-full px-4 pb-20 pt-12 ph:px-24 md:flex-row md:px-48 lg:px-72 xl:px-96">
      <div className="mb-16 flex flex-col items-center gap-12 pb-20">
        <div className="flex flex-col items-center gap-4">
          <span className="text-sm uppercase tracking-wider text-gold">
            Réseaux sociaux
          </span>
          <h3 className="font-cormorant text-4xl text-primary ph:text-5xl">
            Restons connectés
          </h3>
        </div>

        {/* Version mobile (jusqu'à ph) */}
        <p className="max-w-3xl text-center text-sm leading-relaxed text-gray-600 ph:hidden">
          Découvrez nos salons de coiffure à <strong>Villejuif</strong> et{" "}
          <strong>Ville d&apos;Avray</strong>. Notre équipe de{" "}
          <em>coiffeurs-visagistes</em> est à votre écoute sur nos réseaux
          sociaux et via notre formulaire de contact.
        </p>

        {/* Version tablette (ph à sm) */}
        <p className="hidden max-w-3xl text-center text-sm leading-relaxed text-gray-600 ph:block sm:hidden">
          Envie d&apos;en savoir plus sur nos services de coiffure à{" "}
          <strong>Villejuif</strong> et <strong>Ville d&apos;Avray</strong> ?
          <em>coiffeurs-visagistes</em> vous accueille dans nos salons.
          Contactez-nous via le formulaire ou suivez nos actualités sur les
          réseaux sociaux.
        </p>

        {/* Version desktop (sm et plus) */}
        <p className="hidden max-w-3xl text-center text-sm leading-relaxed text-gray-600 sm:block">
          Envie de prendre rendez-vous ou d&apos;en savoir plus sur nos services
          de coiffure à <strong>Villejuif</strong> et{" "}
          <strong>Ville d&apos;Avray</strong> ? Contactez-nous via le formulaire
          ci-dessous ou rejoignez notre communauté sur les réseaux sociaux pour
          découvrir nos dernières créations et actualités. Notre équipe de{" "}
          <em>coiffeurs-visagistes</em> se fera un plaisir de vous répondre dans
          les plus brefs délais.
        </p>

        <div className="flex flex-col items-center gap-8">
          <div className="flex gap-8">
            {socialLinks.map((social) => (
              <Link
                key={social.name}
                href={social.href}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex flex-col items-center gap-3"
              >
                <div className="rounded-full bg-[#191919] p-4 transition-all duration-300 ease-out group-hover:bg-gold">
                  <Icon icon={social.icon} className="h-6 w-6 text-white" />
                </div>
                {/* Version mobile */}
                <span className="text-center text-xs font-medium uppercase tracking-wider text-primary ph:hidden">
                  {social.name === "Instagram"
                    ? "Réalisations"
                    : social.name === "Facebook"
                      ? "Actualités"
                      : "Carrière"}
                </span>
                {/* Version tablette et desktop */}
                <span className="hidden text-center text-xs font-medium uppercase tracking-wider text-primary ph:block">
                  {social.description}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </div>
      <div className="flex w-full flex-col justify-between md:flex-row">
        <h2 className="mb-12 font-cormorant text-4xl text-primary ph:text-5xl sm:text-6xl lg:text-7xl">
          FORMULAIRE DE CONTACT
        </h2>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <label htmlFor="nom" className="mb-2 block text-sm font-medium">
              VOTRE NOM*
            </label>
            <input
              type="text"
              id="nom"
              name="nom"
              required
              className={`w-full border ${
                errors.nom ? "border-red-500" : "border-gray-300"
              } bg-white p-3 text-sm transition-colors focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900`}
              value={formData.nom}
              onChange={handleChange}
            />
            {errors.nom && (
              <p className="mt-1 text-sm text-red-500">{errors.nom}</p>
            )}
          </div>

          <div>
            <label htmlFor="prenom" className="mb-2 block text-sm font-medium">
              VOTRE PRÉNOM*
            </label>
            <input
              type="text"
              id="prenom"
              name="prenom"
              required
              className={`w-full border ${
                errors.prenom ? "border-red-500" : "border-gray-300"
              } bg-white p-3 text-sm transition-colors focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900`}
              value={formData.prenom}
              onChange={handleChange}
            />
            {errors.prenom && (
              <p className="mt-1 text-sm text-red-500">{errors.prenom}</p>
            )}
          </div>

          <div>
            <label htmlFor="email" className="mb-2 block text-sm font-medium">
              EMAIL*
            </label>
            <input
              type="email"
              id="email"
              name="email"
              required
              className={`w-full border ${
                errors.email ? "border-red-500" : "border-gray-300"
              } bg-white p-3 text-sm transition-colors focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900`}
              value={formData.email}
              onChange={handleChange}
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div>
            <label
              htmlFor="telephone"
              className="mb-2 block text-sm font-medium"
            >
              TÉLÉPHONE*
            </label>
            <input
              type="tel"
              id="telephone"
              name="telephone"
              className={`w-full border ${
                errors.telephone ? "border-red-500" : "border-gray-300"
              } bg-white p-3 text-sm transition-colors focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900`}
              value={formData.telephone}
              onChange={handleChange}
            />
            {errors.telephone && (
              <p className="mt-1 text-sm text-red-500">{errors.telephone}</p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor="message" className="mb-2 block text-sm font-medium">
            VOTRE MESSAGE
          </label>
          <textarea
            id="message"
            name="message"
            rows={6}
            placeholder="Écrivez votre message ici..."
            className={`w-full border ${
              errors.message ? "border-red-500" : "border-gray-300"
            } bg-white p-3 text-sm transition-colors focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900`}
            value={formData.message}
            onChange={handleChange}
          />
          {errors.message && (
            <p className="mt-1 text-sm text-red-500">{errors.message}</p>
          )}
        </div>

        <div>
          {status.message && (
            <div
              className={`mb-4 rounded-md p-4 ${
                status.type === "success"
                  ? "bg-green-100 text-green-700"
                  : "bg-red-100 text-red-700"
              }`}
            >
              {status.message}
            </div>
          )}
          <button
            type="submit"
            disabled={isLoading}
            className="group relative inline-block pb-[2px]"
          >
            <span>{isLoading ? "ENVOI EN COURS..." : "ENVOYER"}</span>
            <div className="absolute bottom-0 right-0 h-[1px] w-full bg-black">
              <div className="h-full w-0 bg-gold transition-all duration-300 ease-out group-hover:w-full"></div>
            </div>
          </button>
        </div>
      </form>
    </div>
  );
};

export default ContactForm;
